/**
 * Shared pure helpers for exception filter rows (ReconcileRuleModal + CreateContactRuleModal).
 * Keep UI in each modal; share amount/join validation and flatten/normalize helpers here.
 */
import { stringToNumber } from '../../utils/formatter';

export const INTEGER_EXCEPTION_CURRENCIES = new Set(['CLP', 'JPY', 'COP']);

/** Positive finite amount (no currency rules). Used by DOCUMENT contact exceptions. */
export const isValidExceptionAmount = (amount) => Number.isFinite(amount) && amount > 0;

/** Positive amount respecting integer vs decimal currencies (reconcile exceptions). */
export const isValidExceptionAmountValue = (amount, currency = 'CLP') => {
  if (!Number.isFinite(amount) || amount <= 0) return false;
  if (INTEGER_EXCEPTION_CURRENCIES.has(currency)) return Number.isInteger(amount);
  return Math.abs(amount * 100 - Math.round(amount * 100)) < 1e-8;
};

/**
 * Parse amount input. Integer currencies use Chilean thousands (.); others allow decimal (,).
 * @param {string|number|null|undefined} raw
 * @param {string} [currency='CLP']
 */
export const parseExceptionAmountInput = (raw, currency = 'CLP') => {
  if (raw === '' || raw === null || raw === undefined) return undefined;
  if (INTEGER_EXCEPTION_CURRENCIES.has(currency)) {
    const parsed = stringToNumber(raw);
    if (parsed === '' || parsed === null || parsed === undefined) return undefined;
    const num = Number(parsed);
    return Number.isFinite(num) && num > 0 ? num : undefined;
  }
  const cleaned = String(raw).replace(/[^0-9.,]/g, '');
  if (!cleaned) return undefined;
  const normalized = cleaned.includes(',')
    ? cleaned.replace(/\./g, '').replace(',', '.')
    : cleaned.replace(/,/g, '');
  const num = parseFloat(normalized);
  return Number.isFinite(num) && num > 0 ? num : undefined;
};

/** Join operator for filter at index (first filter has none). */
export const resolveExceptionJoinOperator = (filterIndex, joinOperator) => {
  if (filterIndex === 0) return undefined;
  return joinOperator === 'OR' ? 'OR' : 'AND';
};

/**
 * True if two amount predicates can never both be true (same numeric domain).
 */
export const amountPairIsImpossible = (left, right) => {
  const leftAmt = Number(left.amount);
  const rightAmt = Number(right.amount);
  const leftOp = left.operator;
  const rightOp = right.operator;
  if (!Number.isFinite(leftAmt) || !Number.isFinite(rightAmt)) return false;

  const satisfies = (value, op, bound) => {
    if (op === 'GT') return value > bound;
    if (op === 'LT') return value < bound;
    if (op === 'EQ') return value === bound;
    return false;
  };

  if (leftOp === 'EQ' && rightOp === 'EQ') return leftAmt !== rightAmt;
  if (leftOp === 'EQ') return !satisfies(leftAmt, rightOp, rightAmt);
  if (rightOp === 'EQ') return !satisfies(rightAmt, leftOp, leftAmt);
  if (leftOp === 'GT' && rightOp === 'LT') return leftAmt >= rightAmt;
  if (leftOp === 'LT' && rightOp === 'GT') return leftAmt <= rightAmt;
  return false;
};

/**
 * - prefer_eq: > X AND < X (same bound) → use EQ
 * - prefer_or: > high AND < low → use OR for outside range
 * - generic: other AND contradictions
 */
export const classifyImpossibleAmountPair = (left, right) => {
  const leftAmt = Number(left.amount);
  const rightAmt = Number(right.amount);
  const leftOp = left.operator;
  const rightOp = right.operator;
  const sameBound = leftAmt === rightAmt;
  const isGtLt = (leftOp === 'GT' && rightOp === 'LT') || (leftOp === 'LT' && rightOp === 'GT');
  if (isGtLt && sameBound) return 'prefer_eq';
  if (isGtLt) return 'prefer_or';
  return 'generic';
};

/**
 * Amount filters joined with AND that can never both match.
 * @param {object[]} filters
 * @param {{ isCompleteFilter: (f: object) => boolean, requireSameCurrency?: boolean }} options
 */
export const getImpossibleAmountAndJoins = (filters, options) => {
  const isCompleteFilter = options?.isCompleteFilter;
  const requireSameCurrency = Boolean(options?.requireSameCurrency);
  if (typeof isCompleteFilter !== 'function' || !Array.isArray(filters)) return [];

  const issues = [];
  for (let i = 1; i < filters.length; i += 1) {
    const prev = filters[i - 1];
    const curr = filters[i];
    if (!isCompleteFilter(prev) || !isCompleteFilter(curr)) continue;
    if (prev.kind !== 'AMOUNT' || curr.kind !== 'AMOUNT') continue;
    if ((curr.joinOperator || 'AND') !== 'AND') continue;

    if (requireSameCurrency) {
      const prevCur = String(prev.currency || 'CLP').toUpperCase();
      const currCur = String(curr.currency || 'CLP').toUpperCase();
      if (prevCur !== currCur) continue;
      if (amountPairIsImpossible(prev, curr)) {
        issues.push({
          filterIndex: i,
          currency: currCur,
          kind: classifyImpossibleAmountPair(prev, curr),
        });
      }
      continue;
    }

    if (amountPairIsImpossible(prev, curr)) {
      issues.push({
        filterIndex: i,
        kind: classifyImpossibleAmountPair(prev, curr),
      });
    }
  }
  return issues;
};

/**
 * Same-kind value filters (e.g. DOCUMENT_CODE) joined with AND but different values → impossible.
 */
export const getImpossibleExclusiveValueAndJoins = (filters, options) => {
  const isCompleteFilter = options?.isCompleteFilter;
  const kind = options?.kind;
  if (typeof isCompleteFilter !== 'function' || !kind || !Array.isArray(filters)) return [];

  const issues = [];
  for (let i = 1; i < filters.length; i += 1) {
    const prev = filters[i - 1];
    const curr = filters[i];
    if (!isCompleteFilter(prev) || !isCompleteFilter(curr)) continue;
    if ((curr.joinOperator || 'AND') !== 'AND') continue;
    if (prev.kind !== kind || curr.kind !== kind) continue;
    if (String(prev.value) !== String(curr.value)) {
      issues.push({ filterIndex: i, kind: String(kind).toLowerCase() });
    }
  }
  return issues;
};

/**
 * Legacy multi-row exceptions → one row; first filter of later rows joins with OR.
 * @param {Array<{ filters?: object[] }>|null|undefined} exceptions
 * @param {(filter: object) => object} [mapFilter]
 */
export const flattenExceptionsForEdit = (exceptions, mapFilter = (filter) => filter) => {
  if (!Array.isArray(exceptions) || exceptions.length === 0) return [];
  const filters = [];
  exceptions.forEach((row, rowIndex) => {
    (row?.filters || []).forEach((filter, filterIndex) => {
      const normalized = mapFilter({ ...filter });
      if (filters.length === 0) {
        filters.push({ ...normalized, joinOperator: undefined });
        return;
      }
      const joinOperator =
        rowIndex > 0 && filterIndex === 0
          ? 'OR'
          : (filter.joinOperator === 'OR' ? 'OR' : 'AND');
      filters.push({ ...normalized, joinOperator });
    });
  });
  return filters.length > 0 ? [{ filters }] : [];
};
